import React from 'react'
import useSwr from 'swr'
import { TWETCH_API, type TwetchUser } from './twetch-api'

/// address → Twetch identity, resolved in batches and remembered for the
/// session. `null` records "asked, no Twetch wallet there" so an address is
/// never re-asked; identities themselves don't churn within a session.
const resolved = new Map<string, TwetchUser | null>()

/// Seed an identity the caller already knows — e.g. a feed author whose
/// profile is being opened by their Twetch BTC address, which the batch
/// index may not cover (legacy root keys). Never overwrites a resolved hit.
export function primeIdentity(address: string, user: TwetchUser): void {
	if (!resolved.get(address)) {
		resolved.set(address, user)
	}
}

// A change the batch fetch didn't cause — the viewer just created or edited
// their own profile — has to repaint every surface wearing that identity.
let version = 0
const listeners = new Set<() => void>()
function subscribe(listener: () => void): () => void {
	listeners.add(listener)
	return () => listeners.delete(listener)
}

/// Overwrite what is known about an address (the viewer's own save) and
/// repaint everything reading it.
export function updateIdentity(address: string, user: TwetchUser): void {
	resolved.set(address, { ...resolved.get(address), ...user })
	version += 1
	listeners.forEach((listener) => listener())
}

async function fetchBatch(addresses: string[]): Promise<void> {
	const res = await fetch(`${TWETCH_API}/v1/users/by-addresses`, {
		method: 'POST',
		headers: { 'content-type': 'application/json' },
		body: JSON.stringify({ addresses })
	})
	if (!res.ok) {
		throw new Error(`by-addresses ${res.status}`)
	}
	const body = (await res.json()) as { users?: Record<string, TwetchUser> }
	for (const a of addresses) {
		resolved.set(a, body.users?.[a] ?? null)
	}
}

/// Identities for the given addresses, filling in as batches land. Rows
/// without a Twetch identity simply stay absent — the caller shows the
/// address. A failed batch is not retried this render-cycle (the addresses
/// stay unresolved and re-ask on the next distinct set).
export default function useTwetchIdentities(
	addresses: (string | null | undefined)[]
): Record<string, TwetchUser> {
	const wanted = React.useMemo(() => {
		const set = new Set<string>()
		for (const a of addresses) {
			if (a) {
				set.add(a)
			}
		}
		return [...set].sort()
	}, [addresses])
	const updates = React.useSyncExternalStore(
		subscribe,
		() => version,
		() => 0
	)
	const missing = wanted.filter((a) => !resolved.has(a))
	// READ `data`: SWR v2 tracks which fields a component uses and skips the
	// re-render for unread ones — an unread fetch completed invisibly, and
	// identities only appeared when something ELSE repainted (the tape's 60s
	// clock). `batchDone` both forces that re-render and keys the memo.
	const { data: batchDone } = useSwr(
		missing.length > 0 ? ['twetch-by-addresses', missing.join(',')] : null,
		async () => {
			await fetchBatch(missing)
			return missing.join(',')
		},
		{ revalidateOnFocus: false, shouldRetryOnError: false }
	)
	return React.useMemo(() => {
		const out: Record<string, TwetchUser> = {}
		for (const a of wanted) {
			const u = resolved.get(a)
			if (u) {
				out[a] = u
			}
		}
		return out
		// `batchDone` and `missing.length` make the memo recompute the moment
		// a batch lands (this instance's, or another instance's via the
		// shared `resolved` map shrinking `missing`).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [wanted, batchDone, missing.length, updates])
}
